import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCcw, GraduationCap, ScrollText, Users2,
  Building2, Send, Award, Gauge, ShieldCheck, AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ Icon, label, value, sub, tone = "slate", testid }) {
  const toneMap = {
    slate:   "from-slate-50 to-slate-100 border-slate-200 text-slate-900",
    purple:  "from-purple-50 to-purple-100 border-purple-200 text-purple-900",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    rose:    "from-rose-50 to-rose-100 border-rose-200 text-rose-900",
    sky:     "from-sky-50 to-sky-100 border-sky-200 text-sky-900",
  };
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${toneMap[tone]} p-5`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 opacity-80">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-display leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs opacity-80">{sub}</div>}
    </div>
  );
}

function StatusBreakdown({ title, byStatus, testid }) {
  const entries = Object.entries(byStatus || {});
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</div>
      {entries.length === 0 ? (
        <div className="mt-3 text-sm text-slate-400 italic">No records yet.</div>
      ) : (
        <div className="mt-3 space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
              <span className="font-mono text-slate-900 tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SupervisorTable({ supervisors, cap }) {
  if (!supervisors?.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Supervisor Load</div>
        <div className="mt-3 text-sm text-slate-400 italic">
          No active residency rotations. Cap per supervisor: {cap}.
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Supervisor Load (cap {cap} per supervisor)
        </div>
        <div className="text-xs text-slate-500">
          {supervisors.filter((s) => s.at_capacity).length} at capacity
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left px-5 py-2">Supervisor</th>
            <th className="text-right px-5 py-2">Active</th>
            <th className="text-right px-5 py-2">Headroom</th>
            <th className="text-right px-5 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {supervisors.map((s) => (
            <tr key={s.supervisor_id} className="border-t border-slate-100">
              <td className="px-5 py-2 text-slate-700 font-mono text-xs">{s.supervisor_id}</td>
              <td className="px-5 py-2 text-right tabular-nums">{s.active_resident_count}</td>
              <td className="px-5 py-2 text-right tabular-nums">{s.headroom}</td>
              <td className="px-5 py-2 text-right">
                {s.at_capacity ? (
                  <span className="inline-flex items-center gap-1 text-xs text-rose-800 bg-rose-100 px-2 py-0.5 rounded">
                    <AlertTriangle className="w-3 h-3" /> At capacity
                  </span>
                ) : (
                  <span className="text-xs text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                    Open
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EnrollmentsTable({ perProgram }) {
  const entries = Object.entries(perProgram || {}).sort(
    ([, a], [, b]) => (b.alumni + b.active) - (a.alumni + a.active)
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Enrollments per Program
        </div>
      </div>
      {entries.length === 0 ? (
        <div className="p-5 text-sm text-slate-400 italic">No enrollments recorded yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-5 py-2">Program</th>
              <th className="text-right px-5 py-2">Active</th>
              <th className="text-right px-5 py-2">Alumni</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([slug, counts]) => (
              <tr key={slug} className="border-t border-slate-100">
                <td className="px-5 py-2 font-mono text-xs text-slate-700">{slug}</td>
                <td className="px-5 py-2 text-right tabular-nums">{counts.active || 0}</td>
                <td className="px-5 py-2 text-right tabular-nums">{counts.alumni || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminKuhasDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem("kcis_admin_token");
      const r = await fetch(`${API}/api/admin/kuhas/health-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401 || r.status === 403) {
        setErr("Access denied. Super-admin login required.");
        setData(null);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleString()
    : null;

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-xs text-purple-900 hover:underline flex items-center gap-1"
              data-testid="kuhas-dashboard-back-link"
            >
              <ArrowLeft className="w-3 h-3" /> Admin hub
            </Link>
            <span className="font-display text-lg text-slate-900">KUHAS Health Summary</span>
          </div>
          <button
            onClick={load}
            className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1"
            disabled={loading}
            data-testid="kuhas-dashboard-refresh-btn"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="kuhas-dashboard">
        <div>
          <h1 className="font-display text-4xl text-slate-900">KUHAS Operations</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Read-only observability across Wave 1 &amp; Wave 2 collections. Refresh any time —
            this endpoint performs zero writes and can be polled safely.
          </p>
          {generatedAt && (
            <p className="mt-1 text-xs text-slate-400">Snapshot generated {generatedAt}</p>
          )}
        </div>

        {err && (
          <div
            className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
            data-testid="kuhas-dashboard-error"
          >
            {err}
          </div>
        )}

        {loading && !data && (
          <div className="text-sm text-slate-500" data-testid="kuhas-dashboard-loading">
            Loading…
          </div>
        )}

        {data && (
          <>
            {/* Top row — headline numbers */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                Icon={GraduationCap}
                label="Programs"
                value={`${data.programs.active}/${data.programs.total}`}
                sub={data.programs.inactive > 0 ? `${data.programs.inactive} inactive` : "all active"}
                tone="purple"
                testid="kuhas-stat-programs"
              />
              <StatCard
                Icon={Award}
                label="Cert Queue"
                value={data.certificate_queue.total}
                sub={
                  data.certificate_queue.by_status.pending_admin_approval
                    ? `${data.certificate_queue.by_status.pending_admin_approval} awaiting review`
                    : "nothing pending"
                }
                tone="amber"
                testid="kuhas-stat-cert-queue"
              />
              <StatCard
                Icon={Gauge}
                label="Completion (30d)"
                value={data.completion_events.past_30_days}
                sub={
                  data.completion_events.last_triggered_at
                    ? `last ${new Date(data.completion_events.last_triggered_at).toLocaleDateString()}`
                    : "no events yet"
                }
                tone="emerald"
                testid="kuhas-stat-completion"
              />
              <StatCard
                Icon={ShieldCheck}
                label="CE Compliant"
                value={data.admins_flags.ce_compliance_status.compliant || 0}
                sub={
                  data.admins_flags.ce_compliance_status.delinquent
                    ? `${data.admins_flags.ce_compliance_status.delinquent} delinquent`
                    : "no delinquency"
                }
                tone="sky"
                testid="kuhas-stat-ce-compliant"
              />
            </section>

            {/* Second row — Wave 2 subsystem counts */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                Icon={Building2}
                label="CCT Applications"
                value={data.residency_applications.total}
                sub={
                  data.residency_applications.by_status.pending
                    ? `${data.residency_applications.by_status.pending} pending review`
                    : "no pending apps"
                }
                tone="slate"
                testid="kuhas-stat-residency-apps"
              />
              <StatCard
                Icon={Users2}
                label="Active Supervisors"
                value={data.rotations.supervisors.length}
                sub={`cap ${data.rotations.cap_per_supervisor}/each`}
                tone="slate"
                testid="kuhas-stat-supervisors"
              />
              <StatCard
                Icon={Send}
                label="Referrals"
                value={data.referrals.total}
                sub={
                  data.referrals.by_status.pending
                    ? `${data.referrals.by_status.pending} pending accept`
                    : "no pending referrals"
                }
                tone="slate"
                testid="kuhas-stat-referrals"
              />
              <StatCard
                Icon={ScrollText}
                label="Public Alumni"
                value={data.admins_flags.alumni_public_directory_opt_in_true}
                sub={`of ${data.admins_flags.alumni_status_true} total alumni`}
                tone="slate"
                testid="kuhas-stat-alumni"
              />
            </section>

            {/* Detail row 1 */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatusBreakdown
                title="Certificate Queue — by status"
                byStatus={data.certificate_queue.by_status}
                testid="kuhas-cert-status-breakdown"
              />
              <StatusBreakdown
                title="CCT Applications — by status"
                byStatus={data.residency_applications.by_status}
                testid="kuhas-residency-status-breakdown"
              />
            </section>

            {/* Detail row 2 */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatusBreakdown
                title="Referrals — by status"
                byStatus={data.referrals.by_status}
                testid="kuhas-referrals-status-breakdown"
              />
              <StatusBreakdown
                title="CE Compliance — distribution"
                byStatus={data.admins_flags.ce_compliance_status}
                testid="kuhas-ce-status-breakdown"
              />
            </section>

            {/* Full-width supervisor + program tables */}
            <section>
              <SupervisorTable
                supervisors={data.rotations.supervisors}
                cap={data.rotations.cap_per_supervisor}
              />
            </section>

            <section>
              <EnrollmentsTable perProgram={data.enrollments_per_program} />
            </section>

            <div className="pt-4 text-xs text-slate-400">
              Endpoint: <code className="font-mono">GET /api/admin/kuhas/health-summary</code> ·
              read-only, super_admin only.
            </div>
          </>
        )}
      </main>
    </div>
  );
}
